import React, { useState, useEffect } from 'react';
import { BarChart3, TrendingUp, DollarSign, Users, ShoppingBag, Clock, Music, Filter, ChevronLeft, ChevronRight, Video, Mic, Film } from 'lucide-react';
import axios from 'axios';
import ProfitLineGraph from './ProfitLineGraph';
import { engineers } from '../mock';

const API = '/api';

const AnalyticsDashboard = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profitData, setProfitData] = useState([]);
  const [companyProfitData, setCompanyProfitData] = useState([]);
  const [profitLoading, setProfitLoading] = useState(false);
  const [selectedEngineerIndex, setSelectedEngineerIndex] = useState(0);
  const [dateRange, setDateRange] = useState('30'); // Days to look back
  const [viewMode, setViewMode] = useState('individual'); // 'individual' or 'company'
  const [addonAnalytics, setAddonAnalytics] = useState(null);
  const [addonMonth, setAddonMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });

  useEffect(() => {
    fetchAnalytics();
    fetchProfitData();
    fetchCompanyProfitData();
  }, []);

  useEffect(() => {
    fetchAddonAnalytics();
    // eslint-disable-next-line
  }, [addonMonth]);

  const fetchAddonAnalytics = async () => {
    try {
      const r = await axios.get(`${API}/admin/analytics/addons`, { params: { month: addonMonth } });
      setAddonAnalytics(r.data);
    } catch (e) {
      console.error('addon analytics err', e);
    }
  };

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/analytics`);
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateDateRange = (days) => {
    const endDate = new Date();
    const startDate = new Date();
    
    if (days === 'all') {
      // For all time, use a very old date
      startDate.setFullYear(2020, 0, 1);
    } else {
      startDate.setDate(startDate.getDate() - parseInt(days));
    }
    
    return {
      start: startDate.toISOString().split('T')[0],
      end: endDate.toISOString().split('T')[0]
    };
  };

  const fetchProfitData = async (engineerId = null, days = null) => {
    try {
      setProfitLoading(true);
      const currentEngineerId = engineerId !== null ? engineerId : engineers[selectedEngineerIndex].id;
      const currentDays = days !== null ? days : dateRange;
      
      let url = `${API}/analytics/profit-graph?engineer_id=${currentEngineerId}`;
      
      if (currentDays) {
        const { start, end } = calculateDateRange(currentDays);
        url += `&start_date=${start}&end_date=${end}`;
      }
      
      const response = await axios.get(url);
      setProfitData(response.data.data);
    } catch (error) {
      console.error('Error fetching profit data:', error);
    } finally {
      setProfitLoading(false);
    }
  };

  const fetchCompanyProfitData = async (days = null) => {
    try {
      setProfitLoading(true);
      const currentDays = days !== null ? days : dateRange;
      
      let url = `${API}/analytics/profit-graph`; // No engineer_id = company total
      
      if (currentDays) {
        const { start, end } = calculateDateRange(currentDays);
        url += `?start_date=${start}&end_date=${end}`;
      }
      
      const response = await axios.get(url);
      setCompanyProfitData(response.data.data);
    } catch (error) {
      console.error('Error fetching company profit data:', error);
    } finally {
      setProfitLoading(false);
    }
  };

  const handleEngineerChange = (direction) => {
    const newIndex = direction === 'next' 
      ? (selectedEngineerIndex + 1) % engineers.length 
      : (selectedEngineerIndex - 1 + engineers.length) % engineers.length;
    
    setSelectedEngineerIndex(newIndex);
    fetchProfitData(engineers[newIndex].id, dateRange);
  };

  const handleDateRangeChange = (days) => {
    setDateRange(days);
    if (viewMode === 'individual') {
      fetchProfitData(engineers[selectedEngineerIndex].id, days);
    } else {
      fetchCompanyProfitData(days);
    }
  };

  const toggleViewMode = () => {
    const newMode = viewMode === 'individual' ? 'company' : 'individual';
    setViewMode(newMode);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-16 h-16 border-4 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="text-center py-20 text-gray-400">
        <p>Unable to load analytics data</p>
      </div>
    );
  }

  const StatCard = ({ icon: Icon, title, value, subtitle, color }) => (
    <div className="bg-gradient-to-br from-purple-900/20 to-blue-900/20 backdrop-blur-sm border border-purple-500/20 rounded-xl p-6 hover:border-purple-500/50 transition-all">
      <div className="flex items-start justify-between mb-4">
        <div className={`p-3 rounded-lg bg-gradient-to-br ${color}`}>
          <Icon size={24} className="text-white" />
        </div>
      </div>
      <h3 className="text-3xl font-bold text-white mb-1">{value}</h3>
      <p className="text-gray-400 text-sm">{title}</p>
      {subtitle && <p className="text-xs text-gray-500 mt-2">{subtitle}</p>}
    </div>
  );

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-white mb-2">Analytics Dashboard</h2>
          <p className="text-gray-400">Track your studio performance and revenue</p>
        </div>
        <button
          onClick={fetchAnalytics}
          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center space-x-2"
        >
          <BarChart3 size={16} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Bookings Stats */}
      <div>
        <h3 className="text-xl font-bold text-white mb-4">📅 Booking Statistics</h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            icon={Users}
            title="Today's Bookings"
            value={analytics.bookings.today}
            color="from-blue-600 to-blue-700"
          />
          <StatCard
            icon={Users}
            title="This Week"
            value={analytics.bookings.week}
            color="from-purple-600 to-purple-700"
          />
          <StatCard
            icon={Users}
            title="This Month"
            value={analytics.bookings.month}
            color="from-pink-600 to-pink-700"
          />
          <StatCard
            icon={Users}
            title="Total Bookings"
            value={analytics.bookings.total}
            color="from-green-600 to-green-700"
          />
        </div>
      </div>

      {/* Revenue Stats */}
      <div>
        <h3 className="text-xl font-bold text-white mb-1">💰 Revenue</h3>
        <p className="text-xs text-gray-500 mb-4" data-testid="revenue-explainer">Collected = deposits + paid-in-full (matches Stripe/Wave). Booked value = full session prices incl. balances due in studio.</p>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            icon={DollarSign}
            title="Collected Today"
            value={`$${analytics.revenue.today.toFixed(2)}`}
            subtitle={`Booked value: $${(analytics.booked_value?.today ?? 0).toFixed(2)}`}
            color="from-emerald-600 to-emerald-700"
          />
          <StatCard
            icon={DollarSign}
            title="Collected This Week"
            value={`$${analytics.revenue.week.toFixed(2)}`}
            subtitle={`Booked value: $${(analytics.booked_value?.week ?? 0).toFixed(2)}`}
            color="from-teal-600 to-teal-700"
          />
          <StatCard
            icon={DollarSign}
            title="Collected This Month"
            value={`$${analytics.revenue.month.toFixed(2)}`}
            subtitle={`Booked value: $${(analytics.booked_value?.month ?? 0).toFixed(2)}`}
            color="from-cyan-600 to-cyan-700"
          />
          <StatCard
            icon={TrendingUp}
            title="Total Collected"
            value={`$${analytics.revenue.total.toFixed(2)}`}
            subtitle={`Booked value: $${(analytics.booked_value?.total ?? 0).toFixed(2)}`}
            color="from-indigo-600 to-indigo-700"
          />
        </div>
      </div>

      {/* Add-On Revenue */}
      <div data-testid="addon-analytics-section">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-white flex items-center gap-2">
            🎬 Add-On Revenue
            {addonAnalytics && (
              <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-fuchsia-500/20 border border-fuchsia-400/40 text-fuchsia-200">
                {addonAnalytics.month === 'all' ? 'ALL TIME' : addonAnalytics.month}
              </span>
            )}
          </h3>
          <div className="flex gap-2">
            <input
              type="month"
              value={addonMonth === 'all' ? '' : addonMonth}
              onChange={(e) => setAddonMonth(e.target.value || 'all')}
              data-testid="addon-month-picker"
              className="bg-purple-900/30 border border-purple-500/30 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-purple-400"
            />
            <button
              onClick={() => setAddonMonth('all')}
              data-testid="addon-alltime-btn"
              className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition ${
                addonMonth === 'all'
                  ? 'bg-purple-600 text-white'
                  : 'bg-purple-900/30 border border-purple-500/30 text-gray-300 hover:text-white'
              }`}
            >
              All time
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            icon={DollarSign}
            title="Total Add-On Revenue"
            value={`$${(addonAnalytics?.total_addon_revenue ?? 0).toFixed(2)}`}
            subtitle={`${addonAnalytics?.breakdown?.length ?? 0} unique add-ons sold`}
            color="from-fuchsia-600 to-pink-600"
          />
          <StatCard
            icon={Video}
            title="🎛️ Wick Beats Sold"
            value={addonAnalytics?.featured?.wick_beats?.units_sold ?? 0}
            subtitle={`$${(addonAnalytics?.featured?.wick_beats?.revenue ?? 0).toFixed(2)} · ${addonAnalytics?.featured?.wick_beats?.orders ?? 0} orders`}
            color="from-pink-600 to-rose-600"
          />
          <StatCard
            icon={Mic}
            title="🔥 PB Feature Verses"
            value={addonAnalytics?.featured?.pb_features?.units_sold ?? 0}
            subtitle={`$${(addonAnalytics?.featured?.pb_features?.revenue ?? 0).toFixed(2)} · ${addonAnalytics?.featured?.pb_features?.delivered ?? 0} delivered · ${addonAnalytics?.featured?.pb_features?.pending ?? 0} pending`}
            color="from-violet-600 to-purple-600"
          />
          <StatCard
            icon={Film}
            title="Reels Added"
            value={addonAnalytics?.featured?.reels?.units_sold ?? 0}
            subtitle={`$${(addonAnalytics?.featured?.reels?.revenue ?? 0).toFixed(2)}`}
            color="from-blue-600 to-cyan-600"
          />
        </div>

        {/* Breakdown table for other add-ons (if any) */}
        {addonAnalytics?.breakdown?.length > 0 && (
          <div className="mt-6 bg-purple-900/10 border border-purple-500/20 rounded-xl p-4 overflow-x-auto">
            <div className="text-sm font-semibold text-gray-300 mb-3">Full Add-On Breakdown</div>
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-purple-500/20 text-left">
                  <th className="py-2 px-2 text-gray-400 font-semibold text-xs">Add-On</th>
                  <th className="py-2 px-2 text-gray-400 font-semibold text-xs text-right">Units</th>
                  <th className="py-2 px-2 text-gray-400 font-semibold text-xs text-right">Orders</th>
                  <th className="py-2 px-2 text-gray-400 font-semibold text-xs text-right">Revenue</th>
                </tr>
              </thead>
              <tbody>
                {addonAnalytics.breakdown.map((row) => (
                  <tr key={`${row.addon_id}-${row.addon_name}`} className="border-b border-purple-500/10 hover:bg-purple-500/5">
                    <td className="py-2 px-2 text-white">{row.addon_name}</td>
                    <td className="py-2 px-2 text-right text-gray-300 font-mono">{row.units_sold}</td>
                    <td className="py-2 px-2 text-right text-gray-300 font-mono">{row.orders}</td>
                    <td className="py-2 px-2 text-right text-emerald-300 font-mono font-semibold">${row.revenue.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Beat Purchases */}
      <div>
        <h3 className="text-xl font-bold text-white mb-4">🎵 Beat Purchases</h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            icon={Music}
            title="Today's Purchases"
            value={analytics.beat_purchases.today}
            color="from-violet-600 to-violet-700"
          />
          <StatCard
            icon={Music}
            title="This Week"
            value={analytics.beat_purchases.week}
            color="from-fuchsia-600 to-fuchsia-700"
          />
          <StatCard
            icon={Music}
            title="This Month"
            value={analytics.beat_purchases.month}
            color="from-rose-600 to-rose-700"
          />
          <StatCard
            icon={ShoppingBag}
            title="Total Purchases"
            value={analytics.beat_purchases.total}
            color="from-orange-600 to-orange-700"
          />
        </div>
      </div>

      {/* Beat Revenue */}
      <div>
        <h3 className="text-xl font-bold text-white mb-4">💵 Beat Revenue</h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            icon={DollarSign}
            title="Today's Beat Revenue"
            value={`$${analytics.beat_revenue.today.toFixed(2)}`}
            color="from-amber-600 to-amber-700"
          />
          <StatCard
            icon={DollarSign}
            title="This Week"
            value={`$${analytics.beat_revenue.week.toFixed(2)}`}
            color="from-yellow-600 to-yellow-700"
          />
          <StatCard
            icon={DollarSign}
            title="This Month"
            value={`$${analytics.beat_revenue.month.toFixed(2)}`}
            color="from-lime-600 to-lime-700"
          />
          <StatCard
            icon={TrendingUp}
            title="Total Beat Revenue"
            value={`$${analytics.beat_revenue.total.toFixed(2)}`}
            color="from-green-600 to-green-700"
          />
        </div>
      </div>

      {/* License Breakdown & Top Engineers */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* License Breakdown */}
        <div className="bg-gradient-to-br from-purple-900/20 to-blue-900/20 backdrop-blur-sm border border-purple-500/20 rounded-xl p-6">
          <h3 className="text-lg font-bold text-white mb-4 flex items-center space-x-2">
            <Music size={20} className="text-purple-400" />
            <span>Beat License Breakdown</span>
          </h3>
          <div className="space-y-3">
            {Object.entries(analytics.license_breakdown || {}).map(([license, data]) => (
              <div key={license} className="flex items-center justify-between">
                <span className="text-gray-300 capitalize">{license}</span>
                <div className="flex items-center space-x-3">
                  <span className="text-white font-semibold">{data.count} sales</span>
                  <span className="text-green-400">${data.revenue.toFixed(2)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Top Engineers */}
        <div className="bg-gradient-to-br from-purple-900/20 to-blue-900/20 backdrop-blur-sm border border-purple-500/20 rounded-xl p-6">
          <h3 className="text-lg font-bold text-white mb-4 flex items-center space-x-2">
            <Users size={20} className="text-blue-400" />
            <span>Engineer Performance</span>
          </h3>
          <div className="space-y-3">
            {Object.entries(analytics.engineer_stats || {}).map(([engineer, data]) => (
              <div key={engineer} className="flex items-center justify-between">
                <span className="text-gray-300">{engineer}</span>
                <div className="flex items-center space-x-3">
                  <span className="text-white font-semibold">{data.count} sessions</span>
                  <span className="text-right">
                    <span className="block text-green-400">${data.revenue.toFixed(2)}</span>
                    <span className="block text-[11px] text-gray-500">booked ${(data.booked ?? 0).toFixed(2)}</span>
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Profit Line Graph - Comparative */}
      <div className="bg-gradient-to-br from-purple-900/20 to-blue-900/20 backdrop-blur-sm border border-purple-500/20 rounded-xl p-6">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <h3 className="text-xl font-bold text-white flex items-center gap-2">
              <TrendingUp size={24} className="text-purple-400" />
              {viewMode === 'individual' 
                ? `${engineers[selectedEngineerIndex].name}'s Profit` 
                : 'Company Total Profit'}
            </h3>
            <button
              onClick={toggleViewMode}
              className="px-3 py-1 bg-purple-600/20 hover:bg-purple-600/30 border border-purple-500/30 rounded-lg text-purple-300 text-sm font-medium transition-colors"
            >
              Switch to {viewMode === 'individual' ? 'Company' : 'Individual'}
            </button>
          </div>
          
          <div className="flex flex-wrap items-center gap-2">
            {viewMode === 'individual' && (
              <div className="flex items-center gap-2 bg-black/40 border border-purple-500/30 rounded-lg px-3 py-2">
                <button
                  onClick={() => handleEngineerChange('prev')}
                  className="text-purple-400 hover:text-purple-300 transition-colors"
                >
                  <ChevronLeft size={20} />
                </button>
                <span className="text-white text-sm font-semibold min-w-[120px] text-center">
                  {engineers[selectedEngineerIndex].name}
                </span>
                <button
                  onClick={() => handleEngineerChange('next')}
                  className="text-purple-400 hover:text-purple-300 transition-colors"
                >
                  <ChevronRight size={20} />
                </button>
              </div>
            )}
            
            <select
              value={dateRange}
              onChange={(e) => handleDateRangeChange(e.target.value)}
              className="px-4 py-2 bg-black/40 border border-purple-500/30 rounded-lg text-white text-sm focus:outline-none focus:border-purple-500 cursor-pointer"
            >
              <option value="3">Last 3 Days</option>
              <option value="5">Last 5 Days</option>
              <option value="10">Last 10 Days</option>
              <option value="30">Last 30 Days</option>
              <option value="60">Last 60 Days</option>
              <option value="90">Last 90 Days</option>
              <option value="180">Last 180 Days</option>
              <option value="365">Last 365 Days</option>
              <option value="all">All Time</option>
            </select>
          </div>
        </div>
        
        {profitLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-purple-500"></div>
          </div>
        ) : (
          <ProfitLineGraph 
            data={viewMode === 'individual' ? profitData : companyProfitData} 
            color={viewMode === 'individual' ? '#a855f7' : '#10b981'} 
          />
        )}
      </div>

      {/* Most Booked Time Slots */}
      <div className="bg-gradient-to-br from-purple-900/20 to-blue-900/20 backdrop-blur-sm border border-purple-500/20 rounded-xl p-6">
        <h3 className="text-lg font-bold text-white mb-4 flex items-center space-x-2">
          <Clock size={20} className="text-purple-400" />
          <span>Most Popular Time Slots</span>
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {analytics.top_time_slots.map(([time, count], idx) => (
            <div key={time} className="bg-black/40 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-purple-400 mb-1">{count}</div>
              <div className="text-sm text-gray-400">{time}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Google Analytics Info */}
      <div className="bg-gradient-to-br from-blue-900/20 to-purple-900/20 backdrop-blur-sm border border-blue-500/30 rounded-xl p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-xl font-bold text-white mb-2 flex items-center space-x-2">
              <BarChart3 size={24} className="text-blue-400" />
              <span>Google Analytics Dashboard</span>
            </h3>
            <p className="text-gray-400 text-sm">
              Track website traffic, user behavior, and marketing performance
            </p>
          </div>
          <div className="bg-green-500/20 border border-green-500/30 rounded-lg px-3 py-1">
            <span className="text-green-400 text-xs font-semibold">● TRACKING ACTIVE</span>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-6">
          {/* What's Being Tracked */}
          <div>
            <h4 className="text-white font-semibold mb-3 text-sm">📊 Tracked Events:</h4>
            <ul className="space-y-2 text-sm text-gray-400">
              <li className="flex items-start space-x-2">
                <span className="text-purple-400">•</span>
                <span>Page views & user sessions</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-purple-400">•</span>
                <span>Booking starts & completions</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-purple-400">•</span>
                <span>Newsletter signups & applications</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-purple-400">•</span>
                <span>Review submissions</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-purple-400">•</span>
                <span>Social media & contact clicks</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-purple-400">•</span>
                <span>Package deal inquiries</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-purple-400">•</span>
                <span>Revenue & purchase tracking</span>
              </li>
            </ul>
          </div>

          {/* Key Reports */}
          <div>
            <h4 className="text-white font-semibold mb-3 text-sm">📈 Key Reports to Monitor:</h4>
            <ul className="space-y-2 text-sm text-gray-400">
              <li className="flex items-start space-x-2">
                <span className="text-blue-400">→</span>
                <span><strong className="text-white">Real-time:</strong> Current site visitors</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-blue-400">→</span>
                <span><strong className="text-white">Acquisition:</strong> Traffic sources</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-blue-400">→</span>
                <span><strong className="text-white">Engagement:</strong> Popular pages & events</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-blue-400">→</span>
                <span><strong className="text-white">Monetization:</strong> Revenue & conversions</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-blue-400">→</span>
                <span><strong className="text-white">Demographics:</strong> User location & devices</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="bg-black/30 rounded-lg p-4 mb-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div>
              <div className="text-xs text-gray-500 mb-1">Measurement ID</div>
              <div className="text-sm font-mono text-blue-400">G-ZKTQ7DDM7E</div>
            </div>
            <div>
              <div className="text-xs text-gray-500 mb-1">Status</div>
              <div className="text-sm text-green-400 font-semibold">Active</div>
            </div>
            <div>
              <div className="text-xs text-gray-500 mb-1">Setup Date</div>
              <div className="text-sm text-gray-300">Dec 2, 2024</div>
            </div>
            <div>
              <div className="text-xs text-gray-500 mb-1">Data Retention</div>
              <div className="text-sm text-gray-300">14 months</div>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <a
            href="https://analytics.google.com"
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all text-center font-semibold"
          >
            Open Google Analytics Dashboard →
          </a>
          <a
            href="https://analytics.google.com/analytics/web/#/realtime"
            target="_blank"
            rel="noopener noreferrer"
            className="px-4 py-3 bg-purple-900/30 text-white rounded-lg border border-purple-500/30 hover:border-purple-500 transition-all text-center font-semibold"
          >
            View Real-Time
          </a>
        </div>

        <p className="text-xs text-gray-500 mt-4 text-center">
          💡 Tip: Check Real-time reports to see visitors and events as they happen
        </p>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;